import { TourModel, VehicleModel } from '@core/services/api.service';
import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, Inject, ViewChild, OnChanges, OnDestroy } from '@angular/core';
import { DataSourceConfig } from '@core/models';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { VehiclesState } from '@ngxs-store/vehicles-store/vehicles.state';
import { UiState } from '@ngxs-store/ui-store/ui.state';
import { Observable, Subject } from 'rxjs';
import { AddVehicleAction, LoadVehiclesAction } from '@ngxs-store/vehicles-store/vehicles.action';
import { AssignVehicleAction } from '@ngxs-store/managed-tour-store/managed-tour.actions';
import { UpdateEtaDialogComponent } from '../update-eta-dialog/update-eta-dialog';
import { AddVehicleDialogComponent } from '../add-vehicle-dialog/add-vehicle-dialog.component';
import { takeUntil, distinctUntilChanged, filter, debounceTime } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AddTrailerRegByTourIdAction } from '@ngxs-store/managed-tour-store/managed-tour.actions';
import { GetVehicleByIdAction } from '@ngxs-store/vehicles-store/vehicles.action';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

interface VehicleRegistrationLookupItem {
  carrierName: string;
  plateNumber: string;
  provider: string;
}

interface AssignVehicleOption {
  regNumber: string;
  source: 'existing' | 'lookup';
  vehicle?: VehicleModel;
  carrierName?: string;
  provider?: string;
}

@Component({
  selector: 'app-assign-vehicle-dialog',
  templateUrl: './assign-vehicle-dialog.component.html',
  styleUrls: ['./assign-vehicle-dialog.component.scss']
})
export class AssignVehicleDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Select(VehiclesState.getAllVehicles) vehicles$: Observable<VehicleModel[]>;
  @Select(VehiclesState.getVehicleById) vehicleById$: Observable<VehicleModel>;
  @Select(UiState.getTheme) theme$: Observable<string>;
  @Input() vehicles: VehicleModel[];
  @Input() isLoading: boolean;
  @Input() isMobileMode: boolean;
  @Input() id: number;
  @Output() updateDataSourceConfig = new EventEmitter<DataSourceConfig>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  private onDestroy = new Subject<void>();
  dataSource: MatTableDataSource<VehicleModel> = new MatTableDataSource();

  // Filtered data for vehicle section
  filteredVehicleOptions: AssignVehicleOption[] = [];
  private liveSearchVehicles: VehicleModel[] = [];
  private plateLookupResults: VehicleRegistrationLookupItem[] = [];
  private currentSearchTerm: string = '';

  searchControl = new FormControl();
  trailerSearchControl = new FormControl();

  redirectBack = false;
  tourId: any;
  updateTypeId: any;
  updateTypeName: any;

  // Selected items
  selectedVehicleOption: AssignVehicleOption | null = null;
  selectedTrailer: string = '';

  plannedDeliveryStart: string;
  plannedDeliveryEnd: string;
  plannedLoadingStart: string;
  plannedLoadingEnd: string;
  tour: TourModel;
  amcoId: any;
  targetDateTime: any;
  aheadMinutes: any;
  behindMinutes: any;

  // Theme property
  currentTheme: string = 'light-theme';

  dataSourceConfig: DataSourceConfig = {
    name: 'vehicles-table',
    columns: ['regNumber', 'actions'],
    desktopModeColumns: [],
    mobileModeColumns: ['regNumber', 'actions'],
    pageIndex: 10,
    pageSize: 10,
    pageSizeOptions: [10, 50, 100],
    filter: '',
    filterColumns: ['regNumber'],
    selectedFilterColumns: ['regNumber'],
    sortBy: '',
    sortByOrder: ''
  };

  constructor(private dialog: MatDialog, private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) data: any,
    public dialogRef: MatDialog,
    private store: Store,
    private http: HttpClient) {
    //this.store.dispatch(new LoadVehiclesAction());
    this.id = data.id;
    this.redirectBack = data.redirectBack;

    if (data.vehicleId) {
      this.store.dispatch(new GetVehicleByIdAction(data.vehicleId));
    }

    if (data.trailerRegNumber) {
      this.selectedTrailer = data.trailerRegNumber;
      this.trailerSearchControl.setValue(data.trailerRegNumber);
    } else {
      this.selectedTrailer = '';
    }

    if (data.redirectBack) {
      this.tour = data.tour;
      this.tourId = data.tourId;
      this.updateTypeId = data.updateTypeId;
      this.updateTypeName = data.updateTypeName;
      this.amcoId = data.amcoId;
      this.targetDateTime = data.targetDateTime;
      this.plannedDeliveryStart = new Date(data.plannedDeliveryStart).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      this.plannedDeliveryEnd = new Date(data.plannedDeliveryEnd).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      this.plannedLoadingStart = new Date(data.plannedLoadingStart).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      this.plannedLoadingEnd = new Date(data.plannedLoadingEnd).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  ngOnInit() {
    // Only load vehicles and lookup when searching (no preload)
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => term !== undefined && term !== null)
    ).subscribe(value => {
      const term = (value || '').toString().trim();
      this.currentSearchTerm = term;

      // Only search for vehicles if 3+ chars
      if (term.length >= 3) {
        this.store.dispatch(new LoadVehiclesAction({ searchTerm: term }));
      } else {
        this.liveSearchVehicles = [];
        this.applyMergedVehicleFilter();
      }

      this.loadVehicleRegistrationLookup(term);
    });

    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data: VehicleModel, filter: string) => this.filterPredicate(data, filter);

    this.vehicles$.pipe(takeUntil(this.onDestroy)).subscribe(vehicles => {
      // Only update if searching
      if (this.currentSearchTerm.length >= 3) {
        this.liveSearchVehicles = vehicles || [];
        this.applyMergedVehicleFilter();
      }
    });

    this.vehicleById$.pipe(takeUntil(this.onDestroy)).subscribe(vehicle => {
      if (vehicle) {
        this.selectedVehicleOption = {
          regNumber: vehicle.regNumber || '',
          source: 'existing',
          vehicle
        };
        this.liveSearchVehicles = [vehicle];
        this.applyMergedVehicleFilter();
      }
    });

    this.theme$.pipe(takeUntil(this.onDestroy)).subscribe(theme => {
      this.currentTheme = theme;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.vehicles && changes.vehicles.currentValue) {
      this.dataSource.data = changes.vehicles.currentValue;
    }
  }

  onCancelClick(): void {
    this.clearSelections();
    this.dialogRef.closeAll();
  }

  resetDialog(): void {
    this.searchControl.setValue('');
    this.trailerSearchControl.setValue('');
    this.dataSource.data = [];
    this.filteredVehicleOptions = [];
  }

  clearSelections(): void {
    this.selectedVehicleOption = null;
    this.selectedTrailer = '';
    this.trailerSearchControl.setValue('');
  }

  onAddVehicleClick() {
    this.clearSelections();

    const dialogRef = this.dialogRef.open(AddVehicleDialogComponent, { data: { id: this.id } });

    dialogRef.afterClosed().subscribe(result => { });
  }

  onSaveClick(): void {
    if (this.selectedTrailer && !this.selectedVehicleOption) {
      this.openConfirmationDialog(
        'Trailer Assignment Warning',
        'You are trying to assign a trailer without a vehicle. Are you sure you want to continue?',
        'Yes',
        'No',
        'Assign'
      );
    } else if (this.selectedVehicleOption && !this.selectedTrailer) {
      this.openConfirmationDialog(
        'Vehicle Assignment Warning',
        'Are you sure you want to assign this vehicle without a trailer?',
        'Yes',
        'No',
        'Assign'
      );
    } else if (this.selectedVehicleOption && this.selectedTrailer) {
      this.saveAssignment();
    } else {
      this.saveAssignment();
    }
  }

  private openConfirmationDialog(title: string, message: string, confirmText: string, cancelText: string, buttonType: string): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        modalTitle: title,
        confirmationText: message,
        submitTitle: confirmText,
        cancelBtn: cancelText,
        buttonType: buttonType
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveAssignment();
      }
    });
  }

  private saveAssignment(): void {
    if (!this.selectedVehicleOption) {
      return;
    }

    if (this.selectedVehicleOption.source === 'existing' && this.selectedVehicleOption.vehicle) {
      this.store.dispatch(new AssignVehicleAction(this.id, this.selectedVehicleOption.vehicle.id));
    } else {
      this.store.dispatch(new AddVehicleAction(this.selectedVehicleOption.regNumber, this.id));
    }

    const processedRegNumber = this.selectedTrailer.trim();
    this.store.dispatch(new AddTrailerRegByTourIdAction(this.id, processedRegNumber));

    this.dialogRef.closeAll();
    if (this.redirectBack && this.selectedVehicleOption.source === 'existing' && this.selectedVehicleOption.vehicle) {
      this.updateETA(this.selectedVehicleOption.vehicle);
    }
  }

  assignVehicle(selectedOption: AssignVehicleOption) {
    this.selectedVehicleOption = selectedOption;
  }

  assignTrailer(trailerPlate: string) {
    this.selectedTrailer = trailerPlate;
  }

  onTrailerInputChange(event: any) {
    const value = event.target.value;
    if (!value || value.trim() === '') {
      this.selectedTrailer = '';
      this.trailerSearchControl.setValue('');
    } else {
      this.selectedTrailer = value.trim();
    }
  }

  clearTrailerAssignment() {
    this.selectedTrailer = '';
    this.trailerSearchControl.setValue('');
  }

  removeVehicle() {
    this.selectedVehicleOption = null;
  }

  removeTrailer() {
    this.clearTrailerAssignment();
  }

  applyDataSourceConfig() {
    if (this.dataSource.paginator) {
      this.dataSource.filter = this.dataSourceConfig.filter;
      this.dataSource.paginator.pageIndex = this.dataSourceConfig.pageIndex;
      this.dataSource.paginator.pageSize = this.dataSourceConfig.pageSize;
      this.dataSource.paginator.pageSizeOptions = this.dataSourceConfig.pageSizeOptions;
    }

    if (this.dataSource.sort) {
      this.dataSource.sort.direction = this.dataSourceConfig.sortByOrder;
      this.dataSource.sort.active = this.dataSourceConfig.sortBy;
    }
  }

  page(pageEvent: PageEvent) {
    this.updateDataSourceConfig.emit({ ...this.dataSourceConfig, pageIndex: pageEvent.pageIndex, pageSize: pageEvent.pageSize });
  }

  sortChange(sortEvent: Sort) {
    this.updateDataSourceConfig.emit({ ...this.dataSourceConfig, sortByOrder: sortEvent.direction, sortBy: sortEvent.active });
  }

  updateETA(selectedVehicle: VehicleModel) {
    const dialogRef = this.dialog.open(UpdateEtaDialogComponent, {
      data: {
        tour: this.tour,
        tourId: this.tourId,
        updateTypeId: this.updateTypeId,
        updateTypeName: this.updateTypeName,
        id: this.id,
        amcoId: this.amcoId,
        targetDate: this.targetDateTime,
        hasVehicle: true,
        redirectBack: true,
        plannedDeliveryStart: this.plannedDeliveryStart,
        plannedDeliveryEnd: this.plannedDeliveryEnd,
        plannedLoadingStart: this.plannedLoadingStart,
        plannedLoadingEnd: this.plannedLoadingEnd,
        vehicleId: selectedVehicle.id
      }, width: '800px',
      disableClose: true
    });

    const sub = dialogRef.afterClosed().subscribe(result => { sub.unsubscribe() });
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }


  private mergeAndDedupeVehicles(...lists: VehicleModel[][]): VehicleModel[] {
    const map = new Map<number, VehicleModel>();
    for (const list of lists) {
      for (const vehicle of (list || [])) {
        if (vehicle?.id != null) {
          map.set(vehicle.id, vehicle);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.regNumber || '').localeCompare(b.regNumber || '', undefined, { sensitivity: 'base' })
    );
  }

  private applyMergedVehicleFilter(): void {
    // Only use liveSearchVehicles (no preload)
    const merged = this.mergeAndDedupeVehicles(this.liveSearchVehicles);
    const search = this.currentSearchTerm.toLowerCase();

    const vehicleOptions = merged.map(v => ({
      regNumber: v.regNumber || '',
      source: 'existing' as const,
      vehicle: v
    }));

    const lookupOptions = (this.plateLookupResults || []).map(v => ({
      regNumber: v.plateNumber,
      source: 'lookup' as const,
      carrierName: v.carrierName,
      provider: v.provider
    }));

    const optionMap = new Map<string, AssignVehicleOption>();
    for (const option of [...vehicleOptions, ...lookupOptions]) {
      const key = (option.regNumber || '').toLowerCase().trim();
      if (!key) continue;

      const existing = optionMap.get(key);
      if (!existing || option.source === 'existing') {
        optionMap.set(key, option);
      }
    }

    const combined = Array.from(optionMap.values()).sort((a, b) =>
      (a.regNumber || '').localeCompare(b.regNumber || '', undefined, { sensitivity: 'base' })
    );

    if (!search) {
      this.filteredVehicleOptions = [...combined];
      return;
    }

    this.filteredVehicleOptions = combined.filter(v =>
      (v.regNumber || '').toLowerCase().includes(search) ||
      (v.carrierName || '').toLowerCase().includes(search)
    );
  }

  private rebuildVehicleOptions(): void {
    this.applyMergedVehicleFilter();
  }

  private loadVehicleRegistrationLookup(searchTerm: string): void {
    const term = (searchTerm || '').trim();
    const encoded = encodeURIComponent(term);
    const url = `${environment.webApiBaseAddress}/api/Vehicles/Lookup?searchTerm=${encoded}`;

    this.http.get<VehicleRegistrationLookupItem[]>(url)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((result) => {
        this.plateLookupResults = result || [];
        this.rebuildVehicleOptions();
      });
  }

  filterPredicate(data: VehicleModel, filter: string): boolean {
    return (data.regNumber || '').toLowerCase().includes(filter.trim().toLowerCase());
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
